
import { Component, Vue, Prop, InjectReactive } from "vue-property-decorator"
import { Getter } from "vuex-class"

import { CaseDetails, TemplateDetails, User } from "@/types"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import CaseDetailsHeader from "@/components/CaseDetails/CaseDetailsHeader.vue"
import CaseSideBar from "./CaseSideBar.vue"
import CaseFields from "./CaseFields.vue"
import CaseFiles from "@/components/CaseDetails/CaseFiles.vue"
import KeyEntries from "@/components/CaseDetails/KeyEntries.vue"
import MapDisplay from "@/components/Controls/Map.vue"
import CaseActions from "@/components/CaseDetails/CaseActions.vue"
import History from "./History.vue"
import CCEventModal from "@/components/CaseDetails/CCEvent/CCEventModal.vue"
import TextAreaToggleExpand from "@/components/Fields/TextAreaToggleExpand.vue"
import FormGroup from "@/components/Fields/FormGroup.vue"
import CaseFilePreview from "@/components/CaseDetails/CaseFilePreview.vue"
import MediaPreview from "@/components/CaseDetails/Files/MediaPreview.vue"

import { LayoutLocations } from "@/types/templateLayout"
import CaseFile from "@/types/caseFile"
import { CaseEvents } from "@/types/originatingEvents"

import api from "@/services/api"
import { IntegrationOutOption } from "@/types/integration"
import FileService from "@/services/file-service"
import { formatFieldsMixin } from "@/mixins/formatFieldsMixin"
import { saveCasePropertyWithTracking } from "@/scripts/caseFieldSaving"
import { TenantFeatures } from "@/types/tenantFeatures"

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"case-header": CaseDetailsHeader,
		"case-fields": CaseFields,
		"case-actions": CaseActions,
		"map-display": MapDisplay,
		"case-files": CaseFiles,
		"key-entries": KeyEntries,
		"case-side-bar": CaseSideBar,
		"cc-event-modal": CCEventModal,
		"history": History,
		"text-area-expand": TextAreaToggleExpand,
		"form-group": FormGroup,
		"file-preview": CaseFilePreview,
		"media-preview": MediaPreview
	}
})
export default class Desktop extends Vue {
	@InjectReactive("fileService") fileService!: FileService

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;
	@Getter("isFeatureEnabled") private isFeatureEnabled!: (featureKey: TenantFeatures) => boolean

	@Prop()
	public currentCase!: CaseDetails | null

	@Prop()
	public me!: User

	@Prop({ default: false })
	titleTemplateEnabled!: boolean

	@Prop({ default: false })
	canEditCurrentCase!: boolean

	@Prop({ default: true })
	canCreateComments!: boolean

	@Prop({ default: false })
	editAfterClosePermissions!: boolean

	@Prop()
	template!: TemplateDetails

	@Prop({ default: false })
	preventTemplateChange!: boolean

	@Prop({ default: false })
	hasMap!: boolean

	@Prop({ default: true })
	showAttachments!: boolean

	@Prop({ default: true })
	showTimeline!: boolean

	@Prop({ default: true })
	canUploadFiles!: boolean

	@Prop({ default: true })
	showTitle!: boolean

	@Prop({ default: () => [] })
	caseFiles!: CaseFile[]

	@Prop({ default: () => null })
	public caseEvents!: CaseEvents

	@Prop({ default: () => [] })
	caseIntegrationLinks!: IntegrationOutOption[]

	@Prop({ default: null })
	onLocalFieldUpdate!: ((fieldId: string) => void) | null

	private savingFields: { [key: string]: boolean } = {
		"summary": false
	}
	fieldErrors: { [key: string]: {
		error: string,
		clearTimeout?: ReturnType<typeof setTimeout>
	} } = {}
	private layoutLocations = LayoutLocations;

	/**
	 * Map of built-in fields user has lock on
	 */
	private get allowLockedFieldsToBeEditedEnabled (): boolean {
		return this.isFeatureEnabled(TenantFeatures.AllowLockedFieldsToBeEdited)
	}

	private get lockedForCurrentUser (): { [key: string]: boolean } {
		if (!this.currentCase || !this.currentCase.builtInFieldLocks) {
			return {}
		}

		const locks = this.currentCase.builtInFieldLocks
		return Object.keys(locks).reduce((acc: { [key: string]: boolean }, key: string) => {
			const lock = locks[key]
			if (!lock) {
				acc[key] = false
			} else if (this.allowLockedFieldsToBeEditedEnabled) {
				acc[key] = false
			} else {
				acc[key] = lock.id !== this.me.id
			}
			return acc
		}, {} as { [key: string]: boolean })
	}

	private get attachmentsVisible () {
		return (this.showAttachments && this.canUploadFiles && formatFieldsMixin.methods.showField(this.template, "attachments"))
	}

	templateIdChanged (templateId: string) {
		this.$emit("templateIdChanged", templateId)
	}

	caseChanged () {
		if (this.currentCase && this.currentCase.id) {
			api.saveCase(this.currentCase)
		}
	}

	public makeClosedChanges () {
		this.$emit("makeClosedChanges")
	}

	close () {
		this.$emit("close")
	}

	integrationPush (outputOption: IntegrationOutOption) {
		this.$emit("integrationPush", outputOption)
	}

	private onCaseFieldUpdatedLocally (fieldId: string) {
		if (this.onLocalFieldUpdate) {
			this.onLocalFieldUpdate(fieldId)
		}
	}

	private clearFieldError (fieldId: string) {
		if (this.fieldErrors[fieldId]?.clearTimeout) {
			clearTimeout(this.fieldErrors[fieldId].clearTimeout)
		}

		Vue.delete(this.fieldErrors, fieldId)
	}

	private fieldSetError (fieldId: string, error: string, autoClearMs: number | null = 5000) {
		if (this.fieldErrors[fieldId]) {
			if (this.fieldErrors[fieldId].clearTimeout) {
				clearTimeout(this.fieldErrors[fieldId].clearTimeout)
			}
		}

		if (autoClearMs === null) {
			Vue.set(this.fieldErrors, fieldId, {
				error
			})
			return
		}

		const clearTimeoutRef = setTimeout(() => {
			Vue.delete(this.fieldErrors, fieldId)
		}, autoClearMs)

		Vue.set(this.fieldErrors, fieldId, {
			error,
			clearTimeout: clearTimeoutRef
		})
	}

	private async fieldGotFocus (fieldId: string, title: string = "field") {
		if (this.currentCase!.id && this.canEditCurrentCase) {
			try {
				const result = await api.selectCaseField(this.currentCase!.id, fieldId, true)
				if (result.success) {
					this.clearFieldError(fieldId)
					return
				}

				if (this.allowLockedFieldsToBeEditedEnabled) {
					const inUseByName = result.inUseByName || "another user"
					// Keep this warning visible while the field is focused.
					this.fieldSetError(fieldId, `Field is also in use by ${inUseByName}, changes may be overwritten`, null)
				} else {
					const inUseByName = result.inUseByName || "another user"
					this.fieldSetError(fieldId, `Unable to edit ${title} - In use by ${inUseByName}`)
				}
			} catch (err) {
				this.fieldSetError(fieldId, `Unable to edit ${title} - ${err}`)
				console.error(err)
			}
		}
	}

	private async fieldLostFocus (fieldId: string, newValue: string): Promise<void> {
		this.clearFieldError(fieldId)

		if (this.currentCase!.id && this.canEditCurrentCase) {
			try {
				this.savingFields[fieldId] = true
				await saveCasePropertyWithTracking({
					caseId: this.currentCase!.id,
					fieldId,
					value: newValue,
					onLocalFieldUpdate: this.onLocalFieldUpdate
				})
			} finally {
				this.savingFields[fieldId] = false
			}
		}
	}

	// TODO extract into reusable function
	get recordOrCaseName () {
		if (this.currentCase?.isCase || this.template.alwaysPromoted) {
			return this.getCaseName
		}
		if (this.currentCase?.id) {
			return this.getRecordName
		}

		return `${this.getRecordName}/${this.getCaseName}`
	}

	get templateTitleFormatted () {
		return this.template ? `${this.template.title} ` : ""
	}
}
